import { type FocusEvent, type ReactNode, useState } from "react";
import {
  Control, Controller, FieldPath, FieldValues,
} from "react-hook-form";
import { Label, TextInput } from "flowbite-react";
import { InputPasswordEye } from "@/shared/components/forms/ControlledTextInput/InputPasswordEye";
import { P } from "@/shared/ui/typographies/paragraph";

type ControlledTextInput<T extends FieldValues> = React.InputHTMLAttributes<HTMLInputElement> & {
  id: string
  allowAutoComplete?: boolean
  label: ReactNode
  control: Control<T>
  name: FieldPath<T>
  dataTestId?: string
  withPasswordEye?: boolean
  color?: "failure" | "success" | "base"
  helperText?: string
  warningText?: string
  hideError?: boolean
  /** Transform the value on blur (e.g. strip IBAN spaces), while keeping RHF's onBlur. */
  normalizeOnBlur?: (value: string) => string
};

const autoCompleteAttributes = {
  "data-lpignore": "true",
  "data-1p-ignore": "true",
  "data-bwignore": "true",
  "data-chrome-autocomplete": "off",
  "data-google-autocomplete": "off",
};

export const ControlledTextInput = <T extends FieldValues>({
  id,
  type,
  label,
  control,
  name,
  allowAutoComplete = false,
  required,
  dataTestId,
  withPasswordEye = false,
  color,
  helperText,
  warningText,
  hideError = false,
  normalizeOnBlur,
  onBlur,
  ...props
}: ControlledTextInput<T>) => {
  const autoCompleteProps = allowAutoComplete
    ? { autoComplete: props.autoComplete || "on" }
    : { autoComplete: "off", ...autoCompleteAttributes };

  const isPasswordFieldWithEye = type === "password" && withPasswordEye;

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const passwordType = showPassword ? "text" : type;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const showError = error && !hideError;
        const labelColor = color === "failure" || showError ? "failure" : "default";
        const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
          if (normalizeOnBlur) field.onChange(normalizeOnBlur(event.target.value));
          field.onBlur();
          onBlur?.(event);
        };
        return (
          <div className="w-full">
            <Label htmlFor={id} color={labelColor}>
              {label}
              {required && " *"}
            </Label>
            <div className="relative group">
              <TextInput
                {...autoCompleteProps}
                {...field}
                {...props}
                onBlur={handleBlur}
                id={id}
                type={isPasswordFieldWithEye ? passwordType : type}
                data-testid={dataTestId}
                data-slot="input"
                autoCorrect="off"
                className="form-control pt-2"
                color={color === "failure" || showError ? "failure" : "default"}
              />
              {isPasswordFieldWithEye && <InputPasswordEye showPassword={showPassword} setShowPassword={setShowPassword} />}
            </div>
            {showError
              ? (
                <P className="mt-1 text-sm break-words hyphens-auto max-w-full" color="error">
                  {error.message}
                </P>
              )
              : warningText
                ? (
                  <P className="mt-1 text-sm break-words hyphens-auto max-w-full text-yellow-500 dark:text-yellow-400">
                    {warningText}
                  </P>
                )
                : helperText
                  ? (
                    <P className="mt-1 text-sm break-words hyphens-auto max-w-full text-red-600 dark:text-red-500">
                      {helperText}
                    </P>
                  )
                  : null}
          </div>
        );
      }}
    />
  );
};
